import { useDispatch, useSelector } from "react-redux";
import Card from "../../../../Components/Card/Card";
import { DiscountsTable } from "../../../../Components/Customer/VOSS/Discounts/DiscountsTable";
import { useEffect, useState } from "react";
import customerActions from "../../../../Actions/customerActions";
import discountActions from "../../../../Actions/discountActions";
import CreateCustomerDiscountAgreementModal from "../../../../Components/Modals/CreateCustomerDiscountAgreement/CreateCustomerDiscountAgreementModal";
import { useTranslation } from "react-i18next";
import { Button, ButtonOption } from "../../../../Components/Fundamentals/Button";
import useModal from "../../../../Hooks/useModal";
import Modals from "../../../../Constants/modals";
import customerDiscountAgreementActions from "../../../../Actions/Voss/customerDiscountAgreementActions";
import { VossSubscriptionItemStatus } from "../../../../Constants/VOSS/vossSubscriptionItemStatus";
import ConfirmDeleteModal from "../../../../Components/Modals/ConfirmDeleteModal/ConfirmDeleteModal";
import commonActions from "../../../../Actions/commonActions";

export default function SubscriptionsDiscountContainer() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openModal, isModalOpen, closeModal } = useModal();

    const [selectedSubscriptionDiscounts, setSelectedSubscriptionDiscounts] = useState([]);
    const [selectedCustomerDiscounts, setSelectedCustomerDiscounts] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);

    const customer = useSelector(state => state.CustomerReducer.customer);
    const customerDiscountAgreements = useSelector(state => state.CustomerReducer.customerDiscountAgreements);
    const subscriptions = useSelector(state => state.CustomerReducer.detailedSubscriptions);
    const subscriptionsIsLoading = useSelector(state => state.CommonReducer.dataLoading.detailedSubscriptions);
    const customerDiscountAgreementsIsLoading = useSelector(state => state.CommonReducer.dataLoading.customerDiscountAgreements);

    useEffect(() => {
        dispatch(customerDiscountAgreementActions.getCustomerDiscountAgreements(customer.vossCustomer.id));
        dispatch(customerActions.getDetailedSubscriptions(customer.vossCustomer.id));
    }, [dispatch, customer.vossCustomer.id]);

    const onTerminateDiscounts = () => {
        openModal(Modals.ConfirmDeleteModal);
    };

    const performDelete = () => {
        setIsDeleting(true);

        const promises = [];

        if (selectedSubscriptionDiscounts.length > 0) {
            const promise = dispatch(discountActions.terminateSubscriptionDiscounts(selectedSubscriptionDiscounts, () => {
                dispatch(customerActions.getDetailedSubscriptions(customer.vossCustomer.id));
            }));
            promises.push(promise);
        }

        if (selectedCustomerDiscounts.length > 0) {
            const promise = dispatch(customerDiscountAgreementActions.terminateCustomerDiscountAgreements(customer.vossCustomer.id, selectedCustomerDiscounts, () => {
                dispatch(customerDiscountAgreementActions.getCustomerDiscountAgreements(customer.vossCustomer.id));
            }));
            promises.push(promise);
        }

        Promise.allSettled(promises).then((results) => {
            setIsDeleting(false);
            setSelectedSubscriptionDiscounts([]);
            setSelectedCustomerDiscounts([]);
            closeModal();

            const allSucceeded = results.every(r => r.status === "fulfilled");
            if (allSucceeded) {
                dispatch(commonActions.showSuccess(t("subscriptionsDiscountContainer.terminationSuccess")));
            } else {
                dispatch(commonActions.showWarning(t("subscriptionsDiscountContainer.terminationError")));
            }
        });
    };

    const subscriptionsWithDiscounts = subscriptions?.filter(s => s.status === VossSubscriptionItemStatus.Active && s.discounts?.length > 0) || [];

    const allSubscriptionDiscounts = subscriptionsWithDiscounts.flatMap(subscription =>
        subscription.discounts.map((discount, index) => ({
            ...discount,
            subscription: subscription,
            packageName: subscription.package.name,
            _uniqueId: `${subscription.id}-${discount.discountAgreementId}-${discount.startDate}-${discount.endDate}-${index}`
        }))
    );

    const customerDiscountsWithUniqueId = (customerDiscountAgreements || []).map((discount, index) => ({
        ...discount,
        _uniqueId: `${discount.discountAgreementId}-${discount.startDate}-${discount.endDate}-${index}`
    }));

    const toggleSubscriptionDiscount = (discount) => {
        const item = { 
            subscriptionId: discount.subscription.id, 
            discountId: discount.discountAgreementId,
            _uniqueId: discount._uniqueId 
        };
        const index = selectedSubscriptionDiscounts.findIndex(d => d._uniqueId === item._uniqueId);
        
        if (index > -1) {
            setSelectedSubscriptionDiscounts(selectedSubscriptionDiscounts.filter((_, i) => i !== index));
        } else {
            setSelectedSubscriptionDiscounts([...selectedSubscriptionDiscounts, item]);
        }
    };

    const toggleCustomerDiscount = (discount) => {
        const item = {
            discountAgreementId: discount.discountAgreementId,
            _uniqueId: discount._uniqueId
        };
        const index = selectedCustomerDiscounts.findIndex(d => d._uniqueId === item._uniqueId);
        
        if (index > -1) {
            setSelectedCustomerDiscounts(selectedCustomerDiscounts.filter((_, i) => i !== index));
        } else {
            setSelectedCustomerDiscounts([...selectedCustomerDiscounts, item]);
        }
    };

    const selectAll = () => {
        const totalDiscounts = customerDiscountsWithUniqueId.length + allSubscriptionDiscounts.length;
        const totalSelected = selectedCustomerDiscounts.length + selectedSubscriptionDiscounts.length;
        
        if (totalSelected === totalDiscounts) {
            setSelectedCustomerDiscounts([]);
            setSelectedSubscriptionDiscounts([]);
        } else {
            setSelectedCustomerDiscounts(customerDiscountsWithUniqueId.map(d => ({
                discountAgreementId: d.discountAgreementId,
                _uniqueId: d._uniqueId
            })));
            setSelectedSubscriptionDiscounts(allSubscriptionDiscounts.map(d => ({
                subscriptionId: d.subscription.id,
                discountId: d.discountAgreementId,
                _uniqueId: d._uniqueId
            })));
        }
    };

    const isSubscriptionDiscountSelected = (discount) => {
        return selectedSubscriptionDiscounts.some(d => d._uniqueId === discount._uniqueId);
    };

    const isCustomerDiscountSelected = (discount) => {
        return selectedCustomerDiscounts.some(d => d._uniqueId === discount._uniqueId);
    };

    const hasSelectedDiscounts = selectedSubscriptionDiscounts.length > 0 || selectedCustomerDiscounts.length > 0;

    const getConfirmTitle = () => {
        const total = selectedSubscriptionDiscounts.length + selectedCustomerDiscounts.length;
        if (total === 1) {
            return t("subscriptionsDiscountContainer.terminateDiscountTitle");
        }
        return t("subscriptionsDiscountContainer.terminateDiscountsTitle", { count: total });
    };

    const getConfirmItemName = () => {
        const total = selectedSubscriptionDiscounts.length + selectedCustomerDiscounts.length;
        if (total === 1) {
            if (selectedSubscriptionDiscounts.length === 1) {
                const discount = allSubscriptionDiscounts.find(d => 
                    d._uniqueId === selectedSubscriptionDiscounts[0]._uniqueId
                );
                return discount?.name;
            } else {
                const discount = customerDiscountsWithUniqueId.find(d =>
                    d._uniqueId === selectedCustomerDiscounts[0]._uniqueId
                );
                return discount?.name;
            }
        }
        return t("subscriptionsDiscountContainer.discounts", { count: total });
    };

    const totalDiscounts = (customerDiscountAgreements?.length || 0) + allSubscriptionDiscounts.length;
    const totalSelected = selectedCustomerDiscounts.length + selectedSubscriptionDiscounts.length;
    const allSelected = totalDiscounts > 0 && totalSelected === totalDiscounts;

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Header.Title>{t("subscriptionsDiscountContainer.title")}</Card.Header.Title>
                </Card.Header>
                <Card.Content>
                    <div className="d-grid gap-24">
                        {totalDiscounts > 0 && (
                            <div className="d-flex gap-8">
                                <Button option={ButtonOption.SECONDARY} onClick={selectAll}>
                                    {allSelected ? t("subscriptionsDiscountContainer.deselectAll") : t("subscriptionsDiscountContainer.selectAll")}
                                </Button>
                                {hasSelectedDiscounts && (
                                    <Button option={ButtonOption.DESTRUCTIVE} onClick={onTerminateDiscounts}>
                                        {t("subscriptionsDiscountContainer.terminateSelected", { count: totalSelected })}
                                    </Button>
                                )}
                            </div>
                        )}
                        <div className="d-grid">
                            <div className="d-flex justify-content-between">
                                <h2>{t("subscriptionsDiscountContainer.customerDiscounts")}</h2>
                                <Button option={ButtonOption.PRIMARY} onClick={() => openModal(Modals.CreateCustomerDiscountAgreement)}>{t("common.add")}</Button>
                            </div>
                            <div>
                                <DiscountsTable 
                                    isLoading={customerDiscountAgreementsIsLoading} 
                                    discounts={customerDiscountsWithUniqueId} 
                                    selectedDiscounts={selectedCustomerDiscounts}
                                    onToggleSelect={toggleCustomerDiscount}
                                    isSelected={isCustomerDiscountSelected}
                                />
                            </div>
                        </div>
                        <div>
                            <h2>{t("subscriptionsDiscountContainer.subscriptionDiscounts")}</h2>
                            <div>
                                <DiscountsTable 
                                    isLoading={subscriptionsIsLoading} 
                                    discounts={allSubscriptionDiscounts} 
                                    selectedDiscounts={selectedSubscriptionDiscounts}
                                    onToggleSelect={toggleSubscriptionDiscount}
                                    isSelected={isSubscriptionDiscountSelected}
                                />
                            </div>
                        </div>
                    </div>
                </Card.Content>
            </Card>
            {isModalOpen(Modals.CreateCustomerDiscountAgreement) && <CreateCustomerDiscountAgreementModal />}
            {isModalOpen(Modals.ConfirmDeleteModal) && (
                <ConfirmDeleteModal 
                    title={getConfirmTitle()} 
                    itemName={getConfirmItemName()} 
                    onSubmit={performDelete} 
                    isLoading={isDeleting} 
                />
            )}
        </>
    );
}